import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Leaf, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FBFBF9]" data-testid="forgot-password-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-11 h-11 rounded-xl bg-[#14532D] flex items-center justify-center">
            <Leaf className="w-6 h-6 text-emerald-300" />
          </div>
          <div>
            <p className="font-extrabold text-lg tracking-tight text-stone-900" style={{ fontFamily: "Manrope" }}>Fern Hotels</p>
            <p className="text-[11px] text-stone-500 uppercase tracking-[0.15em]">Audit Management Platform</p>
          </div>
        </div>

        {sent ? (
          <div className="space-y-5" data-testid="forgot-password-sent">
            <div className="flex items-center gap-2 text-[#14532D]">
              <MailCheck className="w-6 h-6" strokeWidth={1.5} />
              <h1 className="text-2xl font-bold tracking-tight text-stone-900">Check your inbox</h1>
            </div>
            <p className="text-sm text-stone-500 leading-relaxed">
              If <span className="font-medium text-stone-700">{email}</span> is registered, we've emailed a password reset link. It's valid for 1 hour and can be used once.
            </p>
            <p className="text-xs text-stone-400">Didn't get it? Check spam, or try again in a few minutes.</p>
            <Button variant="outline" className="w-full" data-testid="back-to-login" onClick={() => navigate("/login")}>
              <ArrowLeft className="w-4 h-4 mr-1.5" />Back to sign in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <h1 className="text-2xl font-bold tracking-tight text-stone-900">Forgot password</h1>
            <p className="text-sm text-stone-500">Enter your account email and we'll send you a reset link.</p>
            <div className="space-y-2">
              <Label htmlFor="fp-email">Email</Label>
              <Input id="fp-email" data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@fernhotels.com" className="bg-white" />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm text-rose-600">{error}</p>}
            <Button data-testid="forgot-submit-button" type="submit" disabled={loading} className="w-full bg-[#14532D] hover:bg-[#166534]">
              {loading ? "Sending..." : "Send reset link"}
            </Button>
            <button type="button" data-testid="back-to-login" onClick={() => navigate("/login")} className="text-sm text-stone-500 hover:text-stone-800 w-full text-center">
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
